import { defineStore } from "pinia";
import { ref, computed } from "vue";
// import { loginUser } from '../services/api' // adjust path if needed
import { logoutUser, getPermissions, fetchCachedFiList, heartbeat } from "../services/api";
import { useFiStore } from "./buildStores";
import { logger } from "../utils/logger";
import router from "../router";


export const useAuthStore = defineStore("authStore", () => {
  const token = ref<string | null>(null);
  const error = ref<string | null>(null);
  const authenticated = ref<boolean>(false);
  const privileges = ref<string | null>(null);
  const loginType = ref<string | null>(null);
  const allowedFis = ref<Array<{ fiNumber: string; fiName: string }>>([]);
  const allFis = ref<Array<{ fiNumber: string; fiName: string }>>([]);
  let heartbeatTimer: ReturnType<typeof setInterval> | null = null;

  const isInternal = computed(() => checkPrivilege("internal"));
  const isAdmin = computed(
    () => checkPrivilege("admin") || checkPrivilege("mobAdmin")
  );

  function checkPrivilege(priv: string) {
    return privileges.value === priv;
  }

  function isInternalUser() {
    return checkPrivilege("internal") || checkPrivilege("mobAdmin") || checkPrivilege("admin");
  }

  function checkSession() {
    return authenticated.value;
  }

  async function authenticate() {
    try {
      const res = await getPermissions();
      if (res) {
        logger.debug("✅ User permission response", res);
        logger.debug("📋 Allowed FIs from API:", res.allowedFis);
        authenticated.value = true;
        privileges.value = res.privileges;
        loginType.value = res.loginType || null;
        allowedFis.value = res.allowedFis || [];
        logger.debug("📋 Allowed FIs in store:", allowedFis.value);

        // Fetch all FIs if user is internal
        if (isInternalUser()) {
          try {
            const allFisData = await fetchCachedFiList();
            allFis.value = allFisData || [];
            logger.debug("📋 All FIs loaded for internal user:", allFis.value.length);
          } catch (err) {
            logger.error("❌ Failed to fetch all FIs:", err);
          }
        }

        // Select the FI from the API response
        const fiStore = useFiStore();
        if (!fiStore.getFiNumber && allowedFis.value.length > 0) {
          // Try to use the root fiNumber from the response
          const fiToSelect = allowedFis.value.find(fi => fi.fiNumber === res.fiNumber)
            ? res.fiNumber
            : allowedFis.value[0].fiNumber; // Fall back to first element

          fiStore.setFiNumber(fiToSelect);
          logger.debug("🔄 Selected FI:", fiToSelect);
        }

        return

      } else {
        console.warn("⚠️ User permission responded with error");
      }
    } catch (err) {
      console.error("❌ Error hitting User permission API:", err);
    }
    authenticated.value = false;
  }

  async function logOff() {
    stopHeartbeat();
    authenticated.value = false;

    try {
      await logoutUser();
      logger.debug("✅ Logged out from backend");
    } catch (err) {
      console.error("❌ Error hitting logout API:", err);
    }
    // clearToken();
    logger.debug("✅ token successful. Response:", token);
  }

  function startHeartbeat() {
    if (heartbeatTimer) return;
    logger.debug("Starting session heartbeat");
    heartbeatTimer = setInterval(async () => {
      try {
        const res = await heartbeat();
        logger.debug("Heartbeat: session active");

        // Detect if the session's login type changed (e.g. logged in as a different type in another tab)
        if (loginType.value && res.loginType && res.loginType !== loginType.value) {
          logger.debug("Heartbeat: session type changed", loginType.value, "->", res.loginType);
          stopHeartbeat();
          authenticated.value = false;
          router.push({ path: "/login", query: { reason: "session_changed" } });
        }
      } catch (err) {
        // Auth errors (401, token_expired, token_blacklisted) are handled
        // by the global Axios interceptor which redirects to login.
        // Only log non-auth errors here.
        const axiosError = err as { response?: { status: number } };
        if (axiosError?.response?.status !== 401) {
          logger.debug("Heartbeat: non-auth error, keeping session alive", err);
        }
      }
    }, 60000);
  }

  function stopHeartbeat() {
    if (heartbeatTimer) {
      clearInterval(heartbeatTimer);
      heartbeatTimer = null;
      logger.debug("Stopped session heartbeat");
    }
  }

  return {
    token,
    error,
    // setToken,
    // clearToken,
    logOff,
    isInternal,
    isAdmin,
    isInternalUser,
    authenticate,
    checkSession,
    startHeartbeat,
    stopHeartbeat,
    allowedFis,
    allFis,
    privileges,
    loginType,
  };
});

// // stores/authStore.ts
// import { defineStore } from 'pinia'
// import { ref } from 'vue'
// // import { oktaAuth } from '@/oktaAuth'
// // import type { IDToken } from '@okta/okta-auth-js'

// export const useAuthStore = defineStore('authStore', () => {
//   const idToken = ref<IDToken | null>(null)
//   const token = ref<boolean>(false);
//   const error = ref<string | null>(null)

//   // Simulated login: just mark the token as present
//   function setToken(jwt: string) {
//     logger.debug('🔐 Setting token:', jwt);
//     token.value = true;
//   }

//   // Simulated logout: clear the token
//   function clearToken() {
//     logger.debug('🚪 Clearing token');
//     token.value = false;
//   }

//   // true token
//   function setIdToken(token: IDToken) {
//     idToken.value = token
//     logger.debug('✅ ID Token stored in Pinia:', token)
//   }

//   function clearAuth() {
//     idToken.value = null
//     logger.debug('🔐 Auth cleared from store')
//   }

//   async function logOff() {
//     try {
//       await oktaAuth.signOut()
//       clearAuth()
//       logger.debug('✅ Logged out from Okta')
//     } catch (err) {
//       console.error('❌ Error during logout:', err)
//       error.value = 'Logout failed'
//     }
//   }

//   return {
//     idToken,
//     error,
//     setIdToken,
//     setToken, // right now setToken is not used; use it depends on user permissions
//     clearToken,
//     clearAuth,
//     logOff
//   }
// })
